import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAxisStyles, useChartColors } from 'src/hooks/colors/useChartColors';
import { TooltipContent } from '../../atoms/tooltips/TooltipContent';
import { useLegendContext } from '../Legend/LegendContext';

export type NewBarLineGraphProps = {
  data: Array<object>;
  xAxisKey: string;
  xAxisFormatter?: (value: number | string) => string;
  yAxisFormatter?: (value: number) => string;
  separateYAxes?: boolean;
  stacked?: boolean;
  percentRange?: boolean;
  barLineIsGradient?: boolean;
};

// Requires legend context
export const NewBarLineGraph: React.FC<NewBarLineGraphProps> = ({
  data,
  xAxisKey,
  xAxisFormatter = (value: number | string) => value.toString(),
  yAxisFormatter = (value: number) => value.toString(),
  separateYAxes = false,
  stacked = false,
  percentRange,
  barLineIsGradient = false,
}) => {
  const { selectedBaseLegendValues, selectedLineLegendValues } =
    useLegendContext();

  const { gridColor } = useChartColors();
  const axisStyles = useAxisStyles();

  return (
    <ResponsiveContainer>
      <ComposedChart data={data}>
        <CartesianGrid
          strokeDasharray="4 4"
          strokeWidth={1}
          stroke={gridColor}
          strokeOpacity={0.4}
          vertical={false}
          fill="transparent"
        />
        <YAxis
          yAxisId="line"
          width={70}
          domain={percentRange ? [0, 100] : undefined}
          tickFormatter={yAxisFormatter}
          {...axisStyles}
          axisLine={false}
        />
        {separateYAxes && (
          <YAxis
            hide
            width={70}
            yAxisId="bar"
            tickFormatter={yAxisFormatter}
            {...axisStyles}
            axisLine={false}
          />
        )}
        <XAxis
          dataKey={xAxisKey}
          tickFormatter={xAxisFormatter}
          {...axisStyles}
          axisLine={false}
        />
        <Tooltip
          offset={25}
          content={
            <TooltipContent
              valueFormatter={yAxisFormatter}
              labelFormatter={xAxisFormatter}
            />
          }
        />

        {selectedBaseLegendValues.map(({ key, color }, index) => {
          const isTopBar = index === selectedBaseLegendValues.length - 1;
          const shouldHaveRadius = stacked ? isTopBar : true;

          return (
            <>
              <defs>
                <linearGradient
                  id={`colorPv_${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop stopColor={color} stopOpacity="0.8" />
                  <stop offset="1" stopColor={color} stopOpacity="0.05" />
                </linearGradient>
              </defs>
              <Bar
                isAnimationActive={false}
                key={`bar_${key}`}
                yAxisId={separateYAxes ? 'bar' : 'line'}
                dataKey={key}
                stackId={stacked ? 'a' : key}
                fill={barLineIsGradient ? `url(#colorPv_${index})` : color}
                radius={shouldHaveRadius ? [8, 8, 0, 0] : undefined}
              />
            </>
          );
        })}

        {selectedLineLegendValues.map(({ key, color }) => {
          return (
            <Line
              isAnimationActive={false}
              key={`line_${key}`}
              yAxisId="line"
              dataKey={key}
              stroke={color}
              dot={false}
              strokeWidth={2}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
